import PropTypes from 'prop-types';

import {
  Card,
  CardBody,
  CardTitle, ClipboardCopy,
  DescriptionList, DescriptionListDescription,
  DescriptionListGroup,
  DescriptionListTerm,
  Title,
  Skeleton,
  Divider,
  ClipboardCopyVariant,
  CardFooter,
  Switch
} from '@patternfly/react-core';
import _ from 'lodash';
import React, { Fragment, useCallback } from 'react';
import { useTranslation } from '../../localization/useTranslation';
import TimeView from 'components/Time/TimeView';

import InlineEditDescription from './InlineEditDescription';

export const EDIT_TYPE = Object.freeze({
  inline: 'inline',
  textarea: 'area',
  switch: 'switch'
});


const DescriptionModel = PropTypes.shape({
  path: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired

});

export const DescriptionCardSkeleton = () => {
  return (
    <Card>
      <CardTitle>
        <Skeleton width={'100%'} height={'28px'}/>
        <Divider />
      </CardTitle>
      <CardBody>
        <Skeleton width={'100%'} height={'224px'} />
      </CardBody>
    </Card>
  );
};

const DescriptionCard = ({ heading, models, item, setItem, isLoading, footer }) => {
  const T = useTranslation();


  const parseValueToComp = useCallback((value, label) => {
    if (_.isBoolean(value)) {
      return <Switch label={label} isChecked={value} hasCheckIcon isDisabled />;
    }
    return value;
  }, []);

  return (<Card>
    { heading && <CardTitle>
      <Title headingLevel="h4" size="xl">{ heading }</Title>
      <Divider />
    </CardTitle>
    }
    <CardBody>
      <DescriptionList >
        <DescriptionListGroup>
          { _.map(models, ({ label, key, path, clipboard, time, editType, CustomComponent, extraInfo }) => {
            const itemValue = _.get(item, path);
            // Transform value if needed
            const value = _.isBoolean(itemValue) ? _.toString(itemValue) : itemValue;
            if (isLoading) {
              return (
                <Fragment key={label + value}>
                  <DescriptionListTerm>{ T.localize(label) }</DescriptionListTerm>
                  <DescriptionListDescription><Skeleton screenreaderText="Loading contents" /></DescriptionListDescription>
                </Fragment>
              );
            }
            if (CustomComponent) {
              return (
                <Fragment key={label + value}>
                  <DescriptionListTerm>{ T.localize(label) }</DescriptionListTerm>
                  <DescriptionListDescription>
                    <CustomComponent />
                  </DescriptionListDescription>
                </Fragment>
              );
            }
            if (time) {
              return (
                <Fragment key={label + value}>
                  <DescriptionListTerm>{ T.localize(label) }</DescriptionListTerm>
                  <DescriptionListDescription>
                    <TimeView value={value} viewType={time}/>
                  </DescriptionListDescription>
                </Fragment>
              );
            }
            if (editType) {
              switch (editType) {
                case EDIT_TYPE.switch:
                  return (
                    <React.Fragment>
                      <DescriptionListTerm>{ T.localize(label) }</DescriptionListTerm>
                      <Switch aria-label={label} hasCheckIcon isChecked={itemValue} onChange={(_event, value) => {
                        let clone = _.cloneDeep(item);
                        _.set(clone, path, value);
                        setItem(clone);
                      }}
                      />
                    </React.Fragment>
                  );
                case EDIT_TYPE.textarea:
                  return (
                    <InlineEditDescription key={label + key} label={label} value={value} onChange={(updatedValue) => {
                      let clone = _.cloneDeep(item);
                      _.set(clone, path, updatedValue);
                      setItem(clone);
                    }}/>
                  );
                case EDIT_TYPE.inline:
                default:
                  return (
                    <InlineEditDescription key={label + key} label={label} value={value} onChange={(updatedValue) => {
                      let clone = _.cloneDeep(item);
                      _.set(clone, path, updatedValue);
                      setItem(clone);
                    }}/>
                  );
              }
            }
            if (clipboard) {
              return (
                <Fragment key={label + value}>
                  <DescriptionListTerm>{ T.localize(label) }</DescriptionListTerm>
                  <DescriptionListDescription>
                    <ClipboardCopy variant={ClipboardCopyVariant.expansion} isCode>
                      { value }
                    </ClipboardCopy>
                  </DescriptionListDescription>
                </Fragment>
              );
            }
            return (
              (itemValue !== undefined) ?
                <Fragment key={label + value}>
                  <DescriptionListTerm>{ T.localize(label) }</DescriptionListTerm>
                  <DescriptionListDescription>{ parseValueToComp(itemValue, label) }{ extraInfo }</DescriptionListDescription>
                </Fragment>
                : null
            );
          }) }
        </DescriptionListGroup>
      </DescriptionList>
    </CardBody>
    { footer
    && <CardFooter>
      <Divider inset={{ default: 'insetMd' }} style={{ paddingBottom: '8px' }}/>
      { footer }
    </CardFooter> }
  </Card>);
};

DescriptionCard.propTypes = {
  item: PropTypes.object.isRequired,
  setItem: PropTypes.func,
  heading: PropTypes.string,
  models: PropTypes.arrayOf(DescriptionModel).isRequired,
  isLoading: PropTypes.bool,
  footer: PropTypes.element
};

export default DescriptionCard;
